import React, { useState } from "react";
import { useDispatch } from "react-redux";

import PageTitle from "../../../layouts/PageTitle";
import {
  appApi,
  useGetNcsModemRecoveryQuery,
  useGetNcsFreelancerPaymentsQuery,
} from "../../../../store/appApi";
import {
  usePartnerCode,
  PartnerPicker,
  panelClass,
  btnGhost,
  fmtDate,
} from "./config";
import {
  downloadFreelancerPaymentTemplate,
  bulkUploadFreelancerPaymentsApi,
  addFreelancerPaymentApi,
} from "../../../../services/netplusCustomerStatus";
import { uiToast } from "./ui";

const errMsg = (e) => e?.response?.data?.message || "Something went wrong.";

const fmtAmount = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

// Freelancer screen — no new data model for "who's untraceable", it's the
// same Untraceable case list from Modem Recovery (state=untraceable, already
// filterable there). This page just shows that list next to a separate,
// unlinked payment ledger for the external freelancers who chase those
// cases, so an admin can visually cross-check the two.
const NcsFreelancer = () => {
  const dispatch = useDispatch();
  const [partnerCode, setPartnerCode] = usePartnerCode();
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = React.useRef(null);

  // Per-customer "Add Payment" — one small inline form, keyed by case id.
  const [payFormFor, setPayFormFor] = useState(null);
  const [payName, setPayName] = useState("");
  const [payAmount, setPayAmount] = useState("");
  const [paySaving, setPaySaving] = useState(false);

  const { data: caseData, isFetching: casesFetching } = useGetNcsModemRecoveryQuery({
    partnerCode,
    state: "untraceable",
    limit: 200,
  });
  const cases = caseData?.cases || [];

  const { data: payData, isFetching: paysFetching } = useGetNcsFreelancerPaymentsQuery({
    partnerCode,
    limit: 200,
  });
  const payments = payData?.payments || [];
  const totalAmount = payData?.totalAmount || 0;

  const refresh = () => dispatch(appApi.util.invalidateTags(["NetplusCustomerStatus"]));

  const handleUploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadBusy(true);
    setUploadResult(null);
    try {
      const res = await bulkUploadFreelancerPaymentsApi({ file, partnerCode });
      setUploadResult(res.data);
      refresh();
      uiToast({
        type: "success",
        title: `${res.data?.created ?? 0} payment(s) added`,
        message: res.data?.skipped?.length
          ? `${res.data.skipped.length} row(s) skipped — see details below.`
          : undefined,
      });
    } catch (err) {
      uiToast({ type: "error", title: "Upload failed", message: errMsg(err) });
    } finally {
      setUploadBusy(false);
    }
  };

  const openPayForm = (c) => {
    setPayFormFor(c._id);
    setPayName("");
    setPayAmount("");
  };
  const closePayForm = () => {
    setPayFormFor(null);
    setPayName("");
    setPayAmount("");
  };
  const savePayment = async (c) => {
    const name = payName.trim();
    const amount = Number(payAmount);
    if (!name) {
      uiToast({ type: "warning", title: "Enter the freelancer's name." });
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      uiToast({ type: "warning", title: "Enter a valid amount." });
      return;
    }
    try {
      setPaySaving(true);
      await addFreelancerPaymentApi({
        partnerCode,
        customerKey: c.customerKey,
        customerName: c.name || c.userId || "",
        name,
        amount,
      });
      closePayForm();
      refresh();
      uiToast({ type: "success", title: "Payment recorded" });
    } catch (err) {
      uiToast({ type: "error", title: "Couldn't save payment", message: errMsg(err) });
    } finally {
      setPaySaving(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Freelancer" motherMenu="Netplus" />

      <section className={panelClass}>
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-5 py-3">
          <h5 className="font-semibold text-gray-700">Untraceable — chased by</h5>
          <PartnerPicker
            value={partnerCode}
            onChange={setPartnerCode}
            className="ml-auto"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3 text-left">Customer</th>
                <th className="px-4 py-3 text-left">Phone</th>
                <th className="px-4 py-3 text-center">Marked untraceable</th>
                <th className="px-4 py-3 text-left">Assigned to</th>
                <th className="px-4 py-3 text-right">Payment</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {casesFetching ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                    Loading…
                  </td>
                </tr>
              ) : cases.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                    No untraceable cases.
                  </td>
                </tr>
              ) : (
                cases.map((c) => (
                  <React.Fragment key={c._id}>
                    <tr>
                      <td className="px-4 py-2.5 font-medium text-gray-800">
                        {c.name || c.userId || "—"}
                      </td>
                      <td className="px-4 py-2.5 text-gray-500">{c.phone || "—"}</td>
                      <td className="px-4 py-2.5 text-center text-gray-500">
                        {fmtDate(c.updatedAt)}
                      </td>
                      <td className="px-4 py-2.5 text-gray-700">
                        {c.assignedTo?.name || (
                          <span className="text-gray-400">Unassigned</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <button
                          type="button"
                          className={btnGhost}
                          onClick={() =>
                            payFormFor === c._id ? closePayForm() : openPayForm(c)
                          }
                        >
                          {payFormFor === c._id ? "Cancel" : "Add Payment"}
                        </button>
                      </td>
                    </tr>
                    {payFormFor === c._id && (
                      <tr className="bg-cyan-50/40">
                        <td colSpan={5} className="px-4 py-3">
                          <div className="flex flex-wrap items-end gap-2">
                            <div>
                              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                Name
                              </label>
                              <input
                                autoFocus
                                className="w-44 rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm"
                                placeholder="Freelancer name"
                                value={payName}
                                onChange={(e) => setPayName(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                Amount
                              </label>
                              <input
                                type="number"
                                min="0"
                                className="w-32 rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm"
                                placeholder="0"
                                value={payAmount}
                                onChange={(e) => setPayAmount(e.target.value)}
                              />
                            </div>
                            <button
                              type="button"
                              className="rounded-lg bg-cyan-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-60"
                              disabled={paySaving}
                              onClick={() => savePayment(c)}
                            >
                              {paySaving ? "Saving…" : "Save"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className={`${panelClass} mt-4`}>
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-5 py-3">
          <h5 className="font-semibold text-gray-700">Freelancer payments</h5>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              className={btnGhost}
              onClick={() => downloadFreelancerPaymentTemplate(partnerCode)}
            >
              Template
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              style={{ display: "none" }}
              onChange={handleUploadFile}
            />
            <button
              type="button"
              className="rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadBusy}
            >
              {uploadBusy ? "Uploading…" : "Upload Excel"}
            </button>
          </div>
        </div>

        {uploadResult && (
          <div className="border-b border-gray-100 bg-gray-50/70 px-5 py-3 text-xs">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-gray-700">
                {uploadResult.created ?? 0} added
                {uploadResult.skipped?.length
                  ? `, ${uploadResult.skipped.length} skipped`
                  : ""}
              </span>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setUploadResult(null)}
              >
                Dismiss
              </button>
            </div>
            {uploadResult.skipped?.length > 0 && (
              <div className="mt-2 max-h-40 space-y-1 overflow-y-auto">
                {uploadResult.skipped.map((sk, i) => (
                  <div key={i} className="text-rose-700">
                    Row {sk.row}
                    {sk.name ? ` (${sk.name})` : ""}: {sk.reason}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Customer</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-center">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paysFetching ? (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-gray-400">
                    Loading…
                  </td>
                </tr>
              ) : payments.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-gray-400">
                    No payments recorded yet.
                  </td>
                </tr>
              ) : (
                payments.map((p) => (
                  <tr key={p._id}>
                    <td className="px-4 py-2.5 font-medium text-gray-800">{p.name}</td>
                    <td className="px-4 py-2.5 text-gray-500">
                      {p.customerName || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">
                      {fmtAmount(p.amount)}
                    </td>
                    <td className="px-4 py-2.5 text-center text-gray-500">
                      {fmtDate(p.createdAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {payments.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-gray-200 bg-gray-50/60">
                  <td className="px-4 py-3 text-right font-semibold text-gray-700" colSpan={2}>
                    Total paid
                  </td>
                  <td className="px-4 py-3 text-right font-bold tabular-nums text-gray-900">
                    {fmtAmount(totalAmount)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </section>
    </>
  );
};

export default NcsFreelancer;
